import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Btn, Container } from "./Ui";

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Solutions", to: "/solutions" },
  { label: "Advertisers", to: "/advertisers" },
  { label: "Publishers", to: "/publishers" },
  { label: "Verticals", to: "/verticals" },
  { label: "Contact", to: "/contact" },
];

export const Logo = ({ className = "h-9 sm:h-10" }) => (
  <img src="/apptarget-logo.webp" alt="AppTarget - Performance Beyond Install" width="778" height="174" className={`w-auto ${className}`} />
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 border-b bg-white/85 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 ${scrolled ? "border-slate-200 shadow-[0_4px_24px_rgba(16,43,80,0.06)]" : "border-transparent"}`}>
      <Container className="flex h-[72px] items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="AppTarget home"><Logo /></Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `relative rounded-full px-3.5 py-2 text-[14px] font-medium transition-colors duration-200 ${isActive ? "text-[#2878E5]" : "text-[#102B50] hover:text-[#2878E5]"}`}>
              {({ isActive }) => (<>{n.label}{isActive && <motion.span layoutId="nav-dot" className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#2878E5]" />}</>)}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Btn to="/contact" data-testid="header-cta-btn" className="!px-5 !py-2.5">Start a Partnership</Btn>
        </div>
        <button data-testid="mobile-menu-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
          onClick={() => setOpen((v) => !v)} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-[#102B50] lg:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </Container>
      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-menu" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }} className="overflow-hidden border-t border-slate-200 bg-white lg:hidden" aria-label="Mobile">
            <Container className="flex flex-col py-4">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) => `border-b border-slate-100 py-3.5 font-display text-lg font-semibold ${isActive ? "text-[#2878E5]" : "text-[#102B50]"}`}>
                  {n.label}
                </NavLink>
              ))}
              <Btn to="/contact" className="mt-5 w-full" data-testid="mobile-cta-btn">Start a Partnership</Btn>
            </Container>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
